// src/pages/AdminPacksPage.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaArrowLeft, FaPercentage, FaPlus, FaTimes, FaToggleOn, FaToggleOff,
  FaEdit, FaTrash, FaEye, FaCalendarAlt, FaChartBar, FaShoppingBag,
  FaImage, FaSpinner, FaPlusCircle, FaMinusCircle, FaInfoCircle
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import Notification from "../components/Notification";
import { API, getImageUrl } from "../config/api";

const PACKS_API = API.PACKS;
const PRODUCTS_API = API.PRODUCTS;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

function AdminPacksPage() {
  const navigate = useNavigate();

  // states
  const [packs, setPacks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formVisible, setFormVisible] = useState(false);
  const [editingPack, setEditingPack] = useState(null);

  // form states
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [banners, setBanners] = useState([]);
  const [bannerPreviews, setBannerPreviews] = useState([]);
  const [existingBanners, setExistingBanners] = useState([]);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [selectedPackProducts, setSelectedPackProducts] = useState([]); // [{ productId, productName, size, price, quantity }]
  
  // product selection helpers
  const [currentProductId, setCurrentProductId] = useState("");
  const [currentSize, setCurrentSize] = useState("");
  const [currentQty, setCurrentQty] = useState(1);

  // notification
  const [notification, setNotification] = useState({ show: false, type: "", message: "" });
  const [submitLoading, setSubmitLoading] = useState(false);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: "", message: "" }), 3000);
  };

  // fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [packsRes, productsRes] = await Promise.all([
        axios.get(`${PACKS_API}/all`, { headers }),
        axios.get(PRODUCTS_API)
      ]);
      setPacks(packsRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      showNotification("error", "Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // auto fill sizes for current selected product
  const selectedProductDetails = products.find(p => p._id === currentProductId);
  useEffect(() => {
    if (selectedProductDetails && selectedProductDetails.variants?.length > 0) {
      setCurrentSize(selectedProductDetails.variants[0].size);
    } else {
      setCurrentSize("");
    }
  }, [currentProductId, selectedProductDetails]);

  // add product component to pack list
  const handleAddProductToPack = () => {
    if (!currentProductId || !currentSize || currentQty <= 0) {
      showNotification("error", "Veuillez sélectionner un produit, une taille et une quantité valide.");
      return;
    }

    const prod = products.find(p => p._id === currentProductId);
    const variant = prod.variants.find(v => v.size === currentSize);
    
    // check if already added
    const existingIndex = selectedPackProducts.findIndex(
      item => item.productId === currentProductId && item.size === currentSize
    );

    if (existingIndex > -1) {
      const updated = [...selectedPackProducts];
      updated[existingIndex].quantity += Number(currentQty);
      setSelectedPackProducts(updated);
    } else {
      setSelectedPackProducts([
        ...selectedPackProducts,
        {
          productId: currentProductId,
          productName: prod.name,
          size: currentSize,
          price: variant.price,
          quantity: Number(currentQty)
        }
      ]);
    }

    // reset helper fields
    setCurrentProductId("");
    setCurrentSize("");
    setCurrentQty(1);
  };

  // remove product from list
  const handleRemoveProductFromPack = (index) => {
    setSelectedPackProducts(selectedPackProducts.filter((_, idx) => idx !== index));
  };

  // calculate live prices
  const originalTotalPrice = selectedPackProducts.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discountAmount = originalTotalPrice * (Number(discountPercentage) || 0) / 100;
  const calculatedPackPrice = Math.max(0, originalTotalPrice - discountAmount);

  // handle banner file selection (multiple)
  const handleBannerChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      showNotification('error', 'Certains fichiers ne sont pas des images.');
    }
    // Validate dimensions for each
    validFiles.forEach(file => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width < 800 || img.height < 300) {
          showNotification('error', `Image "${file.name}" trop petite (${img.width}x${img.height}). 1200x400 recommandé.`);
        }
      };
    });
    setBanners(prev => [...prev, ...validFiles]);
    setBannerPreviews(prev => [...prev, ...validFiles.map(f => URL.createObjectURL(f))]);
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.type.startsWith("image/"));
    if (validFiles.length !== files.length) {
      showNotification("error", "Certains fichiers ne sont pas des images.");
    }
    setImages([...images, ...validFiles]);
    setImagePreviews([...imagePreviews, ...validFiles.map(f => URL.createObjectURL(f))]);
  };

  // Submit pack creation or modification
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showNotification("error", "Le nom du pack est requis.");
      return;
    }
    if (selectedPackProducts.length === 0) {
      showNotification("error", "Le pack doit contenir au moins un produit.");
      return;
    }

    setSubmitLoading(true);
    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);
    formData.append("discountPercentage", Number(discountPercentage) || 0);
    formData.append("price", calculatedPackPrice);
    formData.append("startDate", startDate);
    formData.append("endDate", endDate);
    formData.append("isActive", isActive);
    formData.append("products", JSON.stringify(
      selectedPackProducts.map(item => ({
        productId: item.productId,
        productName: item.productName,
        size: item.size,
        price: item.price,
        quantity: item.quantity
      }))
    ));
    banners.forEach(b => formData.append('banner', b));
    formData.append('existingBanners', JSON.stringify(existingBanners));
    images.forEach(img => formData.append("images", img));
    formData.append("existingImages", JSON.stringify(existingImages));

    try {
      const config = {
        headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() }
      };

      if (editingPack) {
        await axios.put(`${PACKS_API}/${editingPack._id}`, formData, config);
        showNotification("success", "Pack modifié avec succès !");
      } else {
        await axios.post(`${PACKS_API}/add`, formData, config);
        showNotification("success", "Pack créé avec succès !");
      }
      resetForm();
      fetchData();
    } catch (err) {
      console.error("Error saving pack:", err);
      showNotification("error", err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Toggle activation directly
  const handleToggleActive = async (packId, currentActive) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${PACKS_API}/${packId}/toggle`, { isActive: !currentActive }, { headers });
      setPacks(packs.map(p => p._id === packId ? { ...p, isActive: !currentActive } : p));
      showNotification("success", `Pack ${!currentActive ? 'activé' : 'désactivé'} avec succès !`);
    } catch (err) {
      console.error("Error toggling pack active:", err);
      showNotification("error", "Erreur de modification du statut active");
    }
  };

  // Delete pack
  const handleDeletePack = async (packId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce pack ?")) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${PACKS_API}/${packId}`, { headers });
      setPacks(packs.filter(p => p._id !== packId));
      showNotification("success", "Pack supprimé avec succès !");
    } catch (err) {
      console.error("Error deleting pack:", err);
      showNotification("error", "Erreur lors de la suppression du pack");
    }
  };

  // Edit pack setup
  const handleEditPack = (pack) => {
    setEditingPack(pack);
    setName(pack.name);
    setDescription(pack.description || "");
    setDiscountPercentage(pack.discountPercentage || 0);
    setStartDate(pack.startDate ? pack.startDate.substring(0, 16) : "");
    setEndDate(pack.endDate ? pack.endDate.substring(0, 16) : "");
    setIsActive(pack.isActive);
    setSelectedPackProducts(pack.products || []);
    setBanners([]);
    setBannerPreviews([]);
    setExistingBanners(pack.bannerImages || (pack.bannerImage ? [pack.bannerImage] : []));
    setImages([]);
    setImagePreviews([]);
    setExistingImages(pack.images || []);
    setFormVisible(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset form
  const resetForm = () => {
    setEditingPack(null);
    setName("");
    setDescription("");
    setDiscountPercentage("");
    setStartDate("");
    setEndDate("");
    setBanners([]);
    setBannerPreviews([]);
    setExistingBanners([]);
    setImages([]);
    setImagePreviews([]);
    setExistingImages([]);
    setIsActive(true);
    setSelectedPackProducts([]);
    setCurrentProductId("");
    setCurrentSize("");
    setCurrentQty(1);
    setFormVisible(false);
  };

  const handleOpenNewForm = () => {
    resetForm();
    setFormVisible(true);
  };

  // compute global stats
  const totalPacks = packs.length;
  const activePacks = packs.filter(p => p.isActive).length;
  const totalClicks = packs.reduce((sum, p) => sum + (p.clicks || 0), 0);
  const totalSales = packs.reduce((sum, p) => sum + (p.salesCount || 0), 0);
  const averageConversionRate = totalClicks > 0 ? ((totalSales / totalClicks) * 100).toFixed(1) : "0.0";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FCFAED] via-[#FFF5E6] to-[#FCFAED] pt-24 pb-16">
      <Notification
        show={notification.show}
        type={notification.type}
        message={notification.message}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-[var(--primary-color)] hover:text-[var(--secondary-color)] font-semibold transition mb-2"
            >
              <FaArrowLeft /> Retour au dashboard
            </button>
            <PageHeader title="Offres & Packs" subtitle="Gérez vos promotions, réductions de produits et bannières dynamiques." />
          </div>

          {!formVisible && (
            <button
              onClick={handleOpenNewForm}
              className="self-start md:self-center bg-gradient-to-r from-[var(--secondary-color)] to-[var(--primary-color)] text-white px-8 py-3.5 rounded-2xl hover:shadow-xl transform hover:scale-105 transition duration-200 flex items-center gap-2 font-bold"
            >
              <FaPlus /> Créer une offre / Pack
            </button>
          )}
        </div>

        {/* LOADING */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FaSpinner className="text-4xl text-[var(--primary-color)] animate-spin mb-4" />
            <p className="text-gray-500">Chargement des données de promotion...</p>
          </div>
        ) : (
          <>
            {/* STATS WIDGETS */}
            {!formVisible && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                
                <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100 flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-xl shrink-0">
                    <FaEye />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Clics</p>
                    <p className="text-2xl font-black text-gray-800 mt-1">{totalClicks}</p>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center text-xl shrink-0">
                    <FaShoppingBag />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Ventes Packs</p>
                    <p className="text-2xl font-black text-gray-800 mt-1">{totalSales}</p>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100 flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center text-xl shrink-0">
                    <FaChartBar />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Taux de Conv.</p>
                    <p className="text-2xl font-black text-gray-800 mt-1">{averageConversionRate}%</p>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100 flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center text-xl shrink-0">
                    <FaPercentage />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Offres Actives</p>
                    <p className="text-2xl font-black text-gray-800 mt-1">{activePacks} / {totalPacks}</p>
                  </div>
                </div>

              </div>
            )}

            {/* FORMULAIRE (Ajout/Edition) */}
            {formVisible && (
              <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border-2 border-[var(--primary-color)] border-opacity-20 mb-12">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
                  <h3 className="text-2xl font-bold text-gray-800">
                    {editingPack ? "✨ Modifier l'offre / Pack" : "✨ Créer un nouveau Pack Promotionnel"}
                  </h3>
                  <button onClick={resetForm} className="text-gray-400 hover:text-gray-600 transition">
                    <FaTimes size={18} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    
                    {/* Colonne Gauche - Infos de base */}
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Nom du Pack <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={name}
                          onChange={e => setName(e.target.value)}
                          placeholder="Ex: Pack Gourmand Ramadan"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[var(--secondary-color)] outline-none transition"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Description de l'offre</label>
                        <textarea
                          rows="3"
                          value={description}
                          onChange={e => setDescription(e.target.value)}
                          placeholder="Ex: Un assortiment spécial de nos meilleurs Zrir à partager."
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[var(--secondary-color)] outline-none transition resize-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Remise (%)</label>
                          <div className="relative">
                            <span className="absolute right-4 top-3 text-gray-400">%</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={discountPercentage}
                              onChange={e => setDiscountPercentage(e.target.value)}
                              placeholder="15"
                              className="w-full pl-4 pr-8 py-3 border-2 border-gray-200 rounded-xl focus:border-[var(--secondary-color)] outline-none transition"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Actif immédiatement ?</label>
                          <div className="flex items-center h-12">
                            <button
                              type="button"
                              onClick={() => setIsActive(!isActive)}
                              className="text-3xl text-[var(--primary-color)] transition focus:outline-none"
                            >
                              {isActive ? <FaToggleOn className="text-green-500" /> : <FaToggleOff className="text-gray-300" />}
                            </button>
                            <span className="ml-3 text-sm font-semibold text-gray-600">{isActive ? "Oui" : "Non"}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                            <FaCalendarAlt className="text-gray-400" /> Date de Début
                          </label>
                          <input
                            type="datetime-local"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[var(--secondary-color)] outline-none transition text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                            <FaCalendarAlt className="text-gray-400" /> Date de Fin
                          </label>
                          <input
                            type="datetime-local"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[var(--secondary-color)] outline-none transition text-sm"
                          />
                        </div>
                      </div>

                      {/* Upload des bannières dynamiques */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Bannières Publicitaires (S'affichent sur la HomePage)
                        </label>
                        <p className="text-xs text-gray-400 mb-2">Vous pouvez ajouter jusqu'à 5 bannières. Elles défileront sur la page d'accueil.</p>
                        <div className="relative border-2 border-dashed border-gray-300 rounded-2xl p-4 flex flex-col items-center justify-center hover:border-[var(--secondary-color)] transition bg-gray-50 min-h-[140px] group">
                          {existingBanners.length > 0 || bannerPreviews.length > 0 ? (
                            <div className="flex flex-wrap gap-2 w-full">
                              {existingBanners.map((img, idx) => (
                                <div key={`ext-ban-${idx}`} className="relative w-20 h-20 rounded-xl overflow-hidden shadow group/img">
                                  <img src={getImageUrl(img)} alt={`Bannière ${idx}`} className="w-full h-full object-cover" />
                                  <button
                                    type="button"
                                    onClick={() => setExistingBanners(existingBanners.filter((_, i) => i !== idx))}
                                    className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow opacity-0 group-hover/img:opacity-100 transition-opacity z-10"
                                  >
                                    <FaTimes size={10} />
                                  </button>
                                </div>
                              ))}
                              {bannerPreviews.map((img, idx) => (
                                <div key={`new-ban-${idx}`} className="relative w-20 h-20 rounded-xl overflow-hidden shadow group/img">
                                  <img src={img} alt={`Preview bannière ${idx}`} className="w-full h-full object-cover" />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setBanners(banners.filter((_, i) => i !== idx));
                                      setBannerPreviews(bannerPreviews.filter((_, i) => i !== idx));
                                    }}
                                    className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow opacity-0 group-hover/img:opacity-100 transition-opacity z-10"
                                  >
                                    <FaTimes size={10} />
                                  </button>
                                </div>
                              ))}
                              <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-[var(--secondary-color)] relative">
                                <FaPlus className="text-gray-400" />
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={handleBannerChange}
                                  className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              <FaImage className="text-gray-300 text-3xl mb-2" />
                              <p className="text-xs text-gray-400 text-center">Ajouter des bannières pour ce pack (1200x400 recommandé)</p>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleBannerChange}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                              />
                            </>
                          )}
                        </div>
                      </div>

                      {/* Upload Photos supplémentaires */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Photos supplémentaires du Pack
                        </label>
                        <div className="relative border-2 border-dashed border-gray-300 rounded-2xl p-4 flex flex-col items-center justify-center hover:border-[var(--secondary-color)] transition bg-gray-50 min-h-[140px] group">
                          {existingImages.length > 0 || imagePreviews.length > 0 ? (
                            <div className="flex flex-wrap gap-2 w-full">
                              {existingImages.map((img, idx) => (
                                <div key={`ext-${idx}`} className="relative w-20 h-20 rounded-xl overflow-hidden shadow group/img">
                                  <img src={getImageUrl(img)} alt={`Pack img ${idx}`} className="w-full h-full object-cover" />
                                  <button
                                    type="button"
                                    onClick={() => setExistingImages(existingImages.filter((_, i) => i !== idx))}
                                    className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow opacity-0 group-hover/img:opacity-100 transition-opacity z-10"
                                  >
                                    <FaTimes size={10} />
                                  </button>
                                </div>
                              ))}
                              {imagePreviews.map((img, idx) => (
                                <div key={`new-${idx}`} className="relative w-20 h-20 rounded-xl overflow-hidden shadow group/img">
                                  <img src={img} alt={`Preview ${idx}`} className="w-full h-full object-cover" />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setImages(images.filter((_, i) => i !== idx));
                                      setImagePreviews(imagePreviews.filter((_, i) => i !== idx));
                                    }}
                                    className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow opacity-0 group-hover/img:opacity-100 transition-opacity z-10"
                                  >
                                    <FaTimes size={10} />
                                  </button>
                                </div>
                              ))}
                              <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-[var(--secondary-color)] relative">
                                <FaPlus className="text-gray-400" />
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={handleImagesChange}
                                  className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              <FaImage className="text-gray-300 text-3xl mb-2" />
                              <p className="text-xs text-gray-400 text-center">Ajouter plusieurs photos pour ce pack</p>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImagesChange}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                              />
                            </>
                          )}
                        </div>
                      </div>

                    </div>

                    {/* Colonne Droite - Sélection des produits et récapitulatif de prix */}
                    <div className="space-y-6 bg-amber-50/40 rounded-3xl p-5 border border-amber-200/50">
                      
                      <div>
                        <h4 className="text-lg font-bold text-[var(--primary-color)] border-b border-amber-200/50 pb-2 mb-4 flex items-center gap-2">
                          🎁 Produits Inclus dans le Pack
                        </h4>

                        {/* Outil de sélection de produit */}
                        <div className="bg-white rounded-2xl p-4 shadow-sm border border-amber-100 space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Sélectionner Produit</label>
                              <select
                                value={currentProductId}
                                onChange={e => setCurrentProductId(e.target.value)}
                                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-[var(--secondary-color)] outline-none"
                              >
                                <option value="">Choisir un produit...</option>
                                {products.map(p => (
                                  <option key={p._id} value={p._id}>{p.name}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Format / Taille</label>
                              <select
                                value={currentSize}
                                onChange={e => setCurrentSize(e.target.value)}
                                disabled={!currentProductId}
                                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-[var(--secondary-color)] outline-none disabled:opacity-50"
                              >
                                <option value="">Taille...</option>
                                {selectedProductDetails?.variants?.map(v => (
                                  <option key={v.size} value={v.size}>{v.size} ({Number(v.price).toFixed(3)} DT)</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="flex gap-3 items-end">
                            <div className="w-32 shrink-0">
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Quantité</label>
                              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden h-10">
                                <button
                                  type="button"
                                  onClick={() => setCurrentQty(q => Math.max(1, q - 1))}
                                  className="w-10 h-full bg-gray-150 hover:bg-gray-200 text-gray-500 flex items-center justify-center"
                                >
                                  <FaMinusCircle />
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  value={currentQty}
                                  onChange={e => setCurrentQty(Math.max(1, Number(e.target.value)))}
                                  className="w-12 text-center h-full focus:outline-none font-bold text-sm"
                                />
                                <button
                                  type="button"
                                  onClick={() => setCurrentQty(q => q + 1)}
                                  className="w-10 h-full bg-gray-150 hover:bg-gray-200 text-gray-500 flex items-center justify-center"
                                >
                                  <FaPlusCircle />
                                </button>
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={handleAddProductToPack}
                              className="flex-1 h-10 bg-[var(--primary-color)] text-white font-bold rounded-lg hover:bg-[var(--secondary-color)] transition flex items-center justify-center gap-1.5 text-sm"
                            >
                              <FaPlus /> Ajouter au Pack
                            </button>
                          </div>
                        </div>

                        {/* Liste des produits ajoutés au pack */}
                        <div className="mt-4 space-y-2 max-h-48 overflow-y-auto pr-1">
                          {selectedPackProducts.length === 0 ? (
                            <p className="text-center text-sm text-gray-400 py-6 flex items-center justify-center gap-1.5">
                              <FaInfoCircle /> Aucun produit encore inclus.
                            </p>
                          ) : (
                            selectedPackProducts.map((item, idx) => (
                              <div key={idx} className="bg-white rounded-xl p-3 shadow-sm border border-amber-100 flex items-center justify-between gap-3 text-sm">
                                <div className="min-w-0">
                                  <p className="font-bold text-gray-800 truncate">{item.productName}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">Taille: <span className="font-semibold">{item.size}</span> · Quantité: <span className="font-semibold">{item.quantity}</span></p>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <span className="font-bold text-gray-700">{(item.price * item.quantity).toFixed(3)} DT</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveProductFromPack(idx)}
                                    className="text-red-400 hover:text-red-600 transition p-1"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>

                      </div>

                      {/* Évaluation financière / Récapitulatif du prix */}
                      <div className="border-t border-amber-200/50 pt-4 space-y-3">
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>Prix cumulé d'origine :</span>
                          <span className="font-semibold">{originalTotalPrice.toFixed(3)} DT</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>Montant de la remise ({Number(discountPercentage) || 0}%) :</span>
                          <span className="font-semibold text-red-500">-{discountAmount.toFixed(3)} DT</span>
                        </div>
                        <div className="flex justify-between items-center text-base border-t border-dashed border-amber-200/50 pt-3">
                          <span className="font-bold text-gray-800">Prix Promo Final du Pack :</span>
                          <span className="text-xl font-black text-[var(--secondary-color)]">
                            {calculatedPackPrice.toFixed(3)} DT
                          </span>
                        </div>
                      </div>

                    </div>

                  </div>

                  {/* Actions formulaire */}
                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-2.5 border-2 border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 font-semibold transition"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={submitLoading}
                      className="px-8 py-2.5 bg-gradient-to-r from-[var(--secondary-color)] to-[var(--primary-color)] text-white rounded-xl font-bold hover:shadow-lg transition transform hover:scale-102 flex items-center gap-2 disabled:opacity-50"
                    >
                      {submitLoading ? (
                        <><FaSpinner className="animate-spin" /> Enregistrement...</>
                      ) : (
                        editingPack ? "Mettre à jour" : "Créer le Pack"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* LISTE DES PACKS */}
            {packs.length === 0 ? (
              <div className="bg-white rounded-3xl shadow p-12 text-center border border-gray-150">
                <FaPercentage className="text-gray-300 text-5xl mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-800 mb-1">Aucune offre promotionnelle configurée</h4>
                <p className="text-gray-400 text-sm mb-6">Créez votre première promotion ou pack de produits dès maintenant.</p>
                <button
                  onClick={handleOpenNewForm}
                  className="px-6 py-3 bg-[var(--primary-color)] text-white rounded-xl hover:bg-[var(--secondary-color)] transition font-bold"
                >
                  <FaPlus className="inline mr-1" /> Créer une offre
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {packs.map((pack) => {
                  const now = new Date();
                  const isDateActive = (!pack.startDate || new Date(pack.startDate) <= now) &&
                                       (!pack.endDate || new Date(pack.endDate) >= now);
                  const isFullyActive = pack.isActive && isDateActive;

                  return (
                    <div
                      key={pack._id}
                      className={`bg-white rounded-3xl shadow-lg border-2 overflow-hidden transition-all flex flex-col ${
                        isFullyActive 
                          ? "border-green-200" 
                          : (!pack.isActive ? "border-gray-200 opacity-80" : "border-amber-200")
                      }`}
                    >
                      {/* Bannière ou placeholder */}
                      <div className="h-44 relative bg-gray-100 overflow-hidden shrink-0">
                        {pack.bannerImage ? (
                          <img src={getImageUrl(pack.bannerImage)} alt={pack.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-[var(--primary-color)] to-[var(--secondary-color)] opacity-20 flex items-center justify-center">
                            <FaPercentage className="text-5xl text-[var(--primary-color)] opacity-40" />
                          </div>
                        )}
                        
                        <div className="absolute top-4 left-4 flex gap-1.5 flex-wrap">
                          <span className="bg-red-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-md">
                            -{pack.discountPercentage}%
                          </span>

                          <span className={`text-white text-xs px-3 py-1 rounded-full font-bold shadow-md ${
                            isFullyActive ? "bg-green-500" : (!pack.isActive ? "bg-gray-500" : "bg-amber-500")
                          }`}>
                            {isFullyActive ? "Actif & Live" : (!pack.isActive ? "Désactivé" : "Hors Dates")}
                          </span>
                        </div>

                        {/* Statut dates */}
                        {(pack.startDate || pack.endDate) && (
                          <div className="absolute bottom-3 left-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg text-white text-xxs flex items-center justify-between font-medium">
                            <span className="flex items-center gap-1"><FaCalendarAlt /> Début: {pack.startDate ? new Date(pack.startDate).toLocaleDateString() : "immédiat"}</span>
                            <span>Fin: {pack.endDate ? new Date(pack.endDate).toLocaleDateString() : "jamais"}</span>
                          </div>
                        )}
                      </div>

                      {/* Contenu */}
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="mb-4">
                          <h4 className="text-xl font-bold text-gray-800 line-clamp-1">{pack.name}</h4>
                          <p className="text-gray-500 text-sm mt-1 line-clamp-2 min-h-[40px]">{pack.description || "Aucune description"}</p>
                        </div>

                        {/* Produits du pack */}
                        <div className="bg-gray-50 rounded-2xl p-3.5 mb-4 text-xs border border-gray-100 space-y-1.5">
                          <p className="font-bold text-gray-500 uppercase tracking-wider mb-2 text-xxs">Produits inclus :</p>
                          {pack.products?.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center text-gray-700">
                              <span className="font-medium truncate max-w-[200px]">{item.productName} ({item.size})</span>
                              <span className="font-semibold shrink-0">×{item.quantity}</span>
                            </div>
                          ))}
                        </div>

                        {/* Stats mini-widget */}
                        <div className="grid grid-cols-2 gap-4 border-t border-gray-100 py-3 mb-4 text-xs text-gray-600">
                          <div className="flex items-center gap-2">
                            <FaEye className="text-gray-400" />
                            <span>Clics: <strong className="text-gray-800">{pack.clicks || 0}</strong></span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaShoppingBag className="text-gray-400" />
                            <span>Ventes: <strong className="text-gray-800">{pack.salesCount || 0}</strong></span>
                          </div>
                        </div>

                        {/* Prix final & Actions */}
                        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
                          <div>
                            <span className="text-xxs text-gray-400 font-bold uppercase tracking-wider">Tarif Pack</span>
                            <p className="text-2xl font-black text-[var(--secondary-color)]">{Number(pack.price).toFixed(3)} <span className="text-sm font-bold">DT</span></p>
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Toggle Switch */}
                            <button
                              onClick={() => handleToggleActive(pack._id, pack.isActive)}
                              className="text-2xl transition hover:scale-105"
                              title={pack.isActive ? "Désactiver la promotion" : "Activer la promotion"}
                            >
                              {pack.isActive ? <FaToggleOn className="text-green-500" /> : <FaToggleOff className="text-gray-300" />}
                            </button>

                            {/* Edit */}
                            <button
                              onClick={() => handleEditPack(pack)}
                              className="w-9 h-9 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg flex items-center justify-center transition"
                              title="Modifier"
                            >
                              <FaEdit size={14} />
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => handleDeletePack(pack._id)}
                              className="w-9 h-9 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg flex items-center justify-center transition"
                              title="Supprimer"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}

export default AdminPacksPage;
